import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import QnAPanel from "../QnAPanel/QnaPanel";
import { getReportPdfBlob, getReportCallout, getInsights, generateInsights } from "shared/api/function_data";
import { queryKeys } from "shared/lib/queryClient";
import "./ReportPage.css";
import { useUser } from "shared/context/UserContext";
import { usePermissions } from "shared/context/PermissionsContext";

const FALLBACK_CALLOUT = "No callout data available for this report.";

export default function ReportPage({
  theme,
  functionId,
  week = "N/A",
  year = "N/A",
  itemKey,
  label,
}) {
  const [showQnA, setShowQnA] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const { canEdit } = usePermissions();
  const queryClient = useQueryClient();

  const reportName = label || itemKey || "Report";
  const toggleQnA = () => setShowQnA(!showQnA);
  const user = useUser();

  const enabled = !!functionId && !!itemKey && week !== "N/A" && year !== "N/A";

  const parseCalloutText = (data) => {
    if (data && typeof data === "object" && data.text) return data.text;
    if (typeof data === "string") {
      try { const p = JSON.parse(data); if (p?.text) return p.text; } catch {}
      try { const p = JSON.parse(data.replace(/'/g, '"')); if (p?.text) return p.text; } catch {}
      const m = data.match(/['"]text['"]\s*:\s*['"]([^'"]*)['"]/);
      if (m?.[1]) return m[1];
      return data;
    }
    return FALLBACK_CALLOUT;
  };

  // ─── PDF (cached as a Blob; component owns the object-URL lifecycle) ───
  const {
    data: pdfBlob,
    isLoading: pdfQueryLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.reportPdf(functionId, itemKey, week, year),
    queryFn: () => getReportPdfBlob(functionId, itemKey, week, year),
    enabled,
  });

  const [pdfUrl, setPdfUrl] = useState(null);
  useEffect(() => {
    if (!pdfBlob) {
      setPdfUrl(null);
      return;
    }
    const url = URL.createObjectURL(pdfBlob);
    setPdfUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfBlob]);

  // While enabled, treat "fetching + no blob yet" as loading (matches old UX).
  const isLoading = enabled && pdfQueryLoading;

  // ─── Callout (cached) ───
  const { data: calloutRaw, isLoading: calloutQueryLoading } = useQuery({
    queryKey: queryKeys.reportCallout(functionId, itemKey, week, year),
    queryFn: () => getReportCallout(functionId, itemKey, week, year),
    enabled,
  });
  const calloutText = enabled
    ? parseCalloutText(calloutRaw) || FALLBACK_CALLOUT
    : "";
  const isCalloutLoading = enabled && calloutQueryLoading;

  // ─── AI Insights (cached) ───
  const { data: insightsData, isLoading: insightsQueryLoading } = useQuery({
    queryKey: queryKeys.insights(functionId, itemKey, week, year),
    queryFn: () => getInsights(functionId, year, week, itemKey),
    enabled,
  });
  const insights = insightsData?.exists ? insightsData : null;
  const insightsLoading = enabled && insightsQueryLoading;

  // ─── Regenerate insights (mutation; writes the cache on success) ───
  const regenerateMutation = useMutation({
    mutationFn: () => generateInsights(functionId, year, week, itemKey),
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKeys.insights(functionId, itemKey, week, year),
        { ...data, exists: true }
      );
    },
    onError: (err) => {
      alert(`AI generation failed: ${err.message}`);
    },
  });
  const regenerating = regenerateMutation.isPending;
  const handleRegenerate = () => regenerateMutation.mutate();

  const hasCallout = calloutText && calloutText !== FALLBACK_CALLOUT;

  const panelOpen = showQnA || showInsights;

  return (
    <div className={`rp-page ${panelOpen ? "rp-page--panel-open" : ""}`}>
      {/* ─── Callout strip — always visible ─── */}
      {hasCallout && (
        <div className="rp-callout-strip">
          <span className="rp-callout-strip__icon">📌</span>
          <div className="rp-callout-strip__content">
            {isCalloutLoading ? "Loading..." : calloutText}
          </div>
        </div>
      )}

      {/* ─── PDF takes all remaining space ─── */}
      <div className="rp-pdf-area">
        {isLoading ? (
          <div className="rp-state">
            <div className="rp-spinner" />
            <p>Loading report...</p>
          </div>
        ) : isError ? (
          <div className="rp-state">
            <span className="rp-state__icon">⚠️</span>
            <p>Unable to load report for Week {week}, {year}</p>
          </div>
        ) : pdfUrl ? (
          <embed
            src={`${pdfUrl}#zoom=page-width&view=FitH`}
            type="application/pdf"
            className="rp-pdf"
          />
        ) : (
          <div className="rp-state">
            <span className="rp-state__icon">📄</span>
            <p>No report available for <strong>{reportName}</strong></p>
          </div>
        )}
      </div>

      {/* AI Insights Toggle Button — positioned above Q&A */}
      {!showInsights && (
        <button
          className="rp-fab"
          onClick={() => setShowInsights(true)}
          title="AI Insights"
          style={{ bottom: "130px" }}
        >
          🤖
        </button>
      )}

      {/* AI Insights Offcanvas Panel (same style as Q&A) */}
      {showInsights && (
        <div className="qna-backdrop qna-backdrop--visible" onClick={() => setShowInsights(false)} />
      )}
      <div className={`qna-offcanvas ${showInsights ? "qna-offcanvas--open" : ""}`}>
        <div className="qna-header">
          <div className="qna-header-left">
            <span style={{ fontSize: "20px" }}>🤖</span>
            <div>
              <h2 className="qna-title" style={{ margin: 0 }}>AI Insights</h2>
              {insights?.generated_at && (
                <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                  Generated: {insights.generated_at}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {canEdit && (
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                style={{
                  padding: "6px 12px", fontSize: "12px", fontWeight: 600,
                  background: regenerating ? "var(--bg-primary)" : "var(--accent, #f90)",
                  color: regenerating ? "var(--text-muted)" : "#fff",
                  border: "none", borderRadius: "6px", cursor: regenerating ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                {regenerating ? "⏳ Generating..." : "🔄 Regenerate"}
              </button>
            )}
            <button
              onClick={() => setShowInsights(false)}
              style={{
                background: "none", border: "1px solid var(--border)", borderRadius: "6px",
                width: "32px", height: "32px", display: "flex", alignItems: "center",
                justifyContent: "center", cursor: "pointer", fontSize: "16px",
                color: "var(--text-secondary)",
              }}
            >✕</button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          {insightsLoading ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-muted)" }}>
              <div style={{ fontSize: "24px", marginBottom: "12px" }}>⏳</div>
              <p>Loading insights...</p>
            </div>
          ) : insights?.insights ? (
            <div className="rp-insights-markdown">
              <ReactMarkdown>{insights.insights}</ReactMarkdown>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-muted)" }}>
              <div style={{ fontSize: "32px", marginBottom: "12px" }}>🤖</div>
              <p style={{ fontWeight: 600, marginBottom: "8px" }}>No AI insights generated yet</p>
              <p style={{ fontSize: "12px", marginBottom: "16px" }}>
                Upload a PDF and ensure AI is enabled for this report.
              </p>
              {canEdit && (
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  style={{
                    padding: "10px 20px", fontSize: "13px", fontWeight: 600,
                    background: "var(--accent, #f90)", color: "#fff",
                    border: "none", borderRadius: "8px", cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {regenerating ? "Generating..." : "Generate Now"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Q&A */}
      <QnAPanel
        isOpen={showQnA}
        onClose={toggleQnA}
        functionId={functionId}
        itemKey={itemKey}
        week={week}
        year={year}
        userlogin={user.userId}
      />
      {!showQnA && (
        <button className="rp-fab" onClick={toggleQnA} title="Open Q&A">
          💬
        </button>
      )}
    </div>
  );
}
